import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import { DropdownWithSearch, Dropdown } from "hesus-ui-kit";

import BaseTable from "./components/BaseTable";

import {
  TABLE_KICKOFF_LIST,
  FILTER_TYPE_DROPDOWN,
  FILTER_TYPE_MULTI_SELECT_DROPDOWN,
} from "@utils/constants";
import { isEmpty } from "@utils/utils";

import "./EnhancedTable.scss";

const EnhancedTable = (props) => {
  const {
    columns,
    dataURL,
    type,
    extraFilters,
    initialPageSize,
    defaultSortOptions,
    isRefreshDataAgain,
    t,
    selectedFilter,
  } = props;

  const [data, setData] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [skipPageReset, setSkipPageReset] = useState(false);
  const [additionalFilters, setAdditionalFilters] = useState([]);
  const [multiSelectFilters, setMultiSelectFilters] = useState(
    selectedFilter?.length > 0 ? selectedFilter : []
  );
  const [requestData, setRequestData] = useState({});
  const [loading, setLoading] = useState(false);

  const fetchIdRef = useRef(0);

  // We need to keep the table from resetting the pageIndex when we
  // Update data. So we can keep track of that flag with a ref.

  // When our cell renderer calls updateMyData, we'll use
  // the rowIndex, columnId and new value to update the
  // original data
  const updateMyData = (rowIndex, columnId, value) => {
    // We also turn on the flag to not reset the page
    setSkipPageReset(true);
    setData((old) =>
      old.map((row, index) => {
        if (index === rowIndex) {
          return {
            ...old[rowIndex],
            [columnId]: value,
          };
        }
        return row;
      })
    );
  };

  const prepareFormData = (
    pageStart,
    pageSize,
    globalFilter,
    sort,
    appliedFilters
  ) => {
    const formData = new FormData();
    formData.append("start", pageStart);
    formData.append("length", pageSize);
    formData.append("search[value]", globalFilter || "");
    formData.append("search[regex]", false);
    if (sort?.dir) {
      formData.append("order[0][dir]", sort ? sort.dir : "desc");
      formData.append("order[0][column]", sort ? sort.column : 0);
    } else {
      formData.append("order[0][dir]", "desc");
      formData.append("order[0][column]", 0);
    }

    if (appliedFilters && appliedFilters.length > 0) {
      for (const filter of appliedFilters) {
        if (filter.column === "admins") {
          if (filter.value !== "*") {
            if (type === TABLE_KICKOFF_LIST) {
              formData.delete("requestAdmin");
              formData.append(`requestAdmin[]`, filter.value);
            } else {
              formData.delete(`${filter.column}`);
              formData.append(`${filter.column}[]`, filter.value);
            }
          }
        } else if (filter.value !== "*") {
          formData.delete(`${filter.column}`);
          if (filter.value !== "no_filter") {
            formData.append(
              `${filter.column}[]`,
              filter.value !== -1 ? filter.value : 0
            );
          } else {
            formData.append(`${filter.column}`, "");
          }
        } else {
          formData.delete(`${filter.column}`);
          formData.append(`${filter.column}`, "");
        }
      }
    }
    return formData;
  };

  /**
   * Method to fetch set of records based on the filters applied.
   * @param {pageSize} pageSize
   * @param {pageStart} pageStart
   * @param {globalFilter} globalFilter
   * @param {sort} sort
   * @param {appliedFilters} appliedFilters
   */
  const fetchRecords = ({
    pageSize,
    pageStart,
    globalFilter,
    sort,
    appliedFilters,
    currentPage,
  }) => {
    // This will get called when the table needs new data
    // You could fetch your data from literally anywhere,

    // Give this fetch an ID
    fetchIdRef.current += 1;
    const fetchId = fetchIdRef.current;

    // save request data,
    setRequestData({
      pageSize,
      pageStart,
      globalFilter,
      sort,
      appliedFilters,
      currentPage,
    });
    // Set the loading state
    setLoading(true);
    const formData = prepareFormData(
      pageStart,
      pageSize,
      globalFilter,
      sort,
      appliedFilters
    );

    axios
      .post(`${dataURL}`, formData)
      .then((result) => {
        if (fetchId === fetchIdRef.current) {
          setLoading(false);
          setData([...result.data.data]);
          setPageCount(parseInt(result.data.recordsTotal, 10));
          setSkipPageReset(true);
        }
      })
      .catch(() => {
        setLoading(false);
        setData([]);
        setPageCount(0);
        setSkipPageReset(true);
      });
  };

  useEffect(() => {
    if (!isEmpty(requestData) && requestData.currentPage === 1) {
      fetchRecords({
        pageSize: requestData.pageSize,
        pageStart: requestData.pageStart,
        globalFilter: requestData.globalFilter,
        currentPage: requestData.currentPage,
        sort: requestData.sort,
        appliedFilters: [...requestData.appliedFilters, ...additionalFilters],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalFilters, localStorage.getItem("locale")]);
  const onFilterChange = (filter) => {
    const isFilterPresent = additionalFilters.find(
      (additionalFilterItem) => additionalFilterItem.column === filter.name
    );
    if (isFilterPresent) {
      const updatedFilters = additionalFilters.map((additionalFilterItem) => {
        const tempFilter = additionalFilterItem;
        if (additionalFilterItem.name === filter.name) {
          tempFilter.value = filter.value;
          tempFilter.column = filter.name;
        }
        return tempFilter;
      });
      setAdditionalFilters(updatedFilters);
    } else {
      setAdditionalFilters([
        ...additionalFilters,
        { column: filter.name, value: filter.value },
      ]);
    }
  };

  const updateMultiSelectFilters = (name, filter) => {
    const isFilterPresent = multiSelectFilters.find(
      (additionalFilterItem) => additionalFilterItem.column === name
    );
    if (isFilterPresent) {
      const updatedMultiSelectFilters = multiSelectFilters.map(
        (additionalFilterItem) => {
          const tempFilter = additionalFilterItem;
          if (additionalFilterItem.column === name) {
            tempFilter.value = filter;
          }
          return tempFilter;
        }
      );
      setMultiSelectFilters(updatedMultiSelectFilters);
    } else {
      setMultiSelectFilters([
        ...multiSelectFilters,
        { column: name, value: filter },
      ]);
    }
  };

  const onMultiSelectFilterChange = (name, filter) => {
    // modify label with column name.
    const modifiedFilters = filter.map((additionalFilterItem) => {
      const tempFilter = {};
      tempFilter.column = name;
      tempFilter.value = additionalFilterItem.value;
      return tempFilter;
    });
    // select other additional filters.
    const selectedAdditionalFilters = additionalFilters.filter(
      (additionalFilterItem) => additionalFilterItem.column !== name
    );

    // remove currently changed filter from applied filters list.
    const selectedAppliedFilters = requestData.appliedFilters.filter(
      (additionalFilterItem) => additionalFilterItem.column !== name
    );
    setAdditionalFilters([...selectedAdditionalFilters, ...modifiedFilters]);
    setRequestData({ ...requestData, appliedFilters: selectedAppliedFilters });

    // update the multiSelectFilters array.
    updateMultiSelectFilters(name, filter);
  };

  return (
    <BaseTable
      columns={columns}
      data={data}
      setData={setData}
      updateMyData={updateMyData}
      skipPageReset={skipPageReset}
      manualPagination
      pageCount={parseInt(pageCount, 10)}
      controlledPageCount={parseInt(pageCount, 10)}
      fetchData={fetchRecords}
      initialPageSize={initialPageSize}
      defaultSortOptions={defaultSortOptions}
      loading={loading}
      type={type}
      canRefresh={isRefreshDataAgain}
      renderExtraFilters={() => {
        return (
          <>
            {extraFilters.map((filterItem) => {
              if (filterItem.type === FILTER_TYPE_DROPDOWN) {
                return (
                  <Dropdown
                    key={filterItem.name}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...filterItem}
                    value={additionalFilters.find(
                      (additionalFilterItem) =>
                        additionalFilterItem.name === filterItem.name
                    )}
                    onChange={onFilterChange}
                  />
                );
              }
              if (filterItem.type === FILTER_TYPE_MULTI_SELECT_DROPDOWN) {
                return (
                  <DropdownWithSearch
                    key={filterItem.name}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...filterItem}
                    value={
                      multiSelectFilters.find(
                        (additionalFilterItem) =>
                          additionalFilterItem.column === filterItem.name
                      )?.value
                    }
                    onChange={(value) =>
                      onMultiSelectFilterChange(filterItem.name, value)
                    }
                  />
                );
              }
              return null;
            })}
          </>
        );
      }}
    />
  );
};

EnhancedTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  dataURL: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  initialPageSize: PropTypes.number,
  isRefreshDataAgain: PropTypes.bool,
  defaultSortOptions: PropTypes.arrayOf(PropTypes.object),
  extraFilters: PropTypes.arrayOf(PropTypes.object),
  t: PropTypes.func.isRequired,
  selectedFilter: PropTypes.arrayOf({}),
};

EnhancedTable.defaultProps = {
  initialPageSize: 10,
  isRefreshDataAgain: false,
  extraFilters: [],
  defaultSortOptions: [],
  selectedFilter: {},
};

export default withTranslation()(EnhancedTable);
